import { MobileManager } from '../core/MobileManager';

/**
 * VirtualJoystick - Touch-based joystick for mobile movement control
 * Returns normalized x/y values (-1 to 1) for steering and throttle
 */
export class VirtualJoystick {
  private container: HTMLElement;
  private element: HTMLElement;
  private knob: HTMLElement;
  private side: 'left' | 'right';

  private isActive: boolean = false;
  private touchId: number | null = null;
  private centerX: number = 0;
  private centerY: number = 0;
  private currentX: number = 0;
  private currentY: number = 0;

  // Joystick dimensions
  private readonly BASE_SIZE = 120;
  private readonly KNOB_SIZE = 50;
  private readonly MAX_DISTANCE: number;

  constructor(container: HTMLElement, side: 'left' | 'right' = 'left') {
    this.container = container;
    this.side = side;
    this.MAX_DISTANCE = (this.BASE_SIZE - this.KNOB_SIZE) / 2;

    this.element = this.createJoystickElement();
    this.knob = this.element.querySelector('.joystick-knob') as HTMLElement;

    this.container.appendChild(this.element);

    this.bindEvents();
    this.updateVisibility();

    // Listen for resize to update visibility
    window.addEventListener('resize', this.updateVisibility);
  }

  private createJoystickElement(): HTMLElement {
    const scale = MobileManager.getScale();
    const size = this.BASE_SIZE * scale;
    const knobSize = this.KNOB_SIZE * scale;

    const wrapper = document.createElement('div');
    wrapper.className = 'virtual-joystick';
    wrapper.innerHTML = `
      <div class="joystick-base">
        <div class="joystick-knob"></div>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.id = 'virtual-joystick-styles';

    // Only add if not already present
    if (!document.getElementById('virtual-joystick-styles')) {
      style.textContent = `
        .virtual-joystick {
          position: fixed;
          bottom: 30px;
          ${this.side}: 30px;
          z-index: 1000;
          touch-action: none;
          user-select: none;
          -webkit-user-select: none;
          display: none;
        }

        .virtual-joystick.visible {
          display: block;
        }

        .joystick-base {
          width: ${size}px;
          height: ${size}px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.3);
          border: 3px solid rgba(0, 255, 255, 0.6);
          box-shadow: 0 0 20px rgba(0, 255, 255, 0.3),
                      inset 0 0 30px rgba(0, 255, 255, 0.1);
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
        }

        .joystick-knob {
          width: ${knobSize}px;
          height: ${knobSize}px;
          border-radius: 50%;
          background: radial-gradient(circle at 30% 30%,
            rgba(0, 255, 255, 0.9) 0%,
            rgba(0, 200, 200, 0.7) 50%,
            rgba(0, 150, 150, 0.5) 100%);
          border: 2px solid rgba(0, 255, 255, 0.8);
          box-shadow: 0 0 15px rgba(0, 255, 255, 0.5),
                      0 4px 8px rgba(0, 0, 0, 0.3);
          position: absolute;
          transition: transform 0.05s ease-out;
        }

        .joystick-knob.active {
          box-shadow: 0 0 25px rgba(0, 255, 255, 0.8),
                      0 4px 8px rgba(0, 0, 0, 0.3);
        }

        /* Direction indicators */
        .joystick-base::before {
          content: '';
          position: absolute;
          width: 8px;
          height: 8px;
          border-top: 2px solid rgba(0, 255, 255, 0.4);
          border-right: 2px solid rgba(0, 255, 255, 0.4);
          top: 10px;
          left: 50%;
          transform: translateX(-50%) rotate(-45deg);
        }

        .joystick-base::after {
          content: '';
          position: absolute;
          width: 8px;
          height: 8px;
          border-bottom: 2px solid rgba(0, 255, 255, 0.4);
          border-left: 2px solid rgba(0, 255, 255, 0.4);
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%) rotate(-45deg);
        }
      `;
      document.head.appendChild(style);
    }

    return wrapper;
  }

  private bindEvents(): void {
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);
    document.addEventListener('touchcancel', this.handleTouchEnd);
  }

  private handleTouchStart = (e: TouchEvent): void => {
    e.preventDefault();

    if (this.touchId !== null) return; // Already tracking a touch

    const touch = e.changedTouches[0];
    this.touchId = touch.identifier;
    this.isActive = true;

    const rect = this.element.querySelector('.joystick-base')!.getBoundingClientRect();
    this.centerX = rect.left + rect.width / 2;
    this.centerY = rect.top + rect.height / 2;

    this.updateKnobPosition(touch.clientX, touch.clientY);
    this.knob.classList.add('active');
  };

  private handleTouchMove = (e: TouchEvent): void => {
    if (this.touchId === null) return;

    // Find our tracked touch
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier === this.touchId) {
        e.preventDefault();
        this.updateKnobPosition(touch.clientX, touch.clientY);
        break;
      }
    }
  };

  private handleTouchEnd = (e: TouchEvent): void => {
    if (this.touchId === null) return;

    // Check if our tracked touch ended
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.touchId) {
        this.touchId = null;
        this.isActive = false;
        this.currentX = 0;
        this.currentY = 0;
        this.knob.style.transform = 'translate(0px, 0px)';
        this.knob.classList.remove('active');
        break;
      }
    }
  };

  private updateKnobPosition(touchX: number, touchY: number): void {
    const scale = MobileManager.getScale();
    const maxDist = this.MAX_DISTANCE * scale;

    let deltaX = touchX - this.centerX;
    let deltaY = touchY - this.centerY;

    // Calculate distance from center
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Clamp to max distance
    if (distance > maxDist) {
      deltaX = (deltaX / distance) * maxDist;
      deltaY = (deltaY / distance) * maxDist;
    }

    // Update visual position
    this.knob.style.transform = `translate(${deltaX}px, ${deltaY}px)`;

    // Normalize values to -1 to 1
    this.currentX = deltaX / maxDist;
    this.currentY = -deltaY / maxDist; // Invert Y so up is positive
  }

  private updateVisibility = (): void => {
    if (MobileManager.isTouchDevice()) {
      this.element.classList.add('visible');
    } else {
      this.element.classList.remove('visible');
    }
  };

  /**
   * Get horizontal value (-1 to 1)
   * Negative = left, Positive = right
   */
  public getX(): number {
    return this.currentX;
  }

  /**
   * Get vertical value (-1 to 1)
   * Negative = backward, Positive = forward
   */
  public getY(): number {
    return this.currentY;
  }

  /**
   * Check if joystick is currently being touched
   */
  public getIsActive(): boolean {
    return this.isActive;
  }

  /**
   * Force show/hide the joystick
   */
  public setVisible(visible: boolean): void {
    if (visible) {
      this.element.classList.add('visible');
    } else {
      this.element.classList.remove('visible');
    }
  }

  /**
   * Cleanup and remove the joystick
   */
  public destroy(): void {
    this.element.removeEventListener('touchstart', this.handleTouchStart);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('touchcancel', this.handleTouchEnd);
    window.removeEventListener('resize', this.updateVisibility);

    this.element.remove();
  }
}
